import type { SlideChapterDefinition } from '@/components/SlideWizard'
import { useLocale, type Locale } from '@/lib/locale'
import { resolveSite } from '@/lib/site'
import { localizeCopy } from '@/lib/spelling'
import { landscapeOfDeceptionChapter } from '@/learning-paths/incode/landscape-of-deception/chapter'
import { emotionalHijackChapter } from '@/learning-paths/incode/emotional-hijack/chapter'
import { pauseAndVerifyChapter } from '@/learning-paths/incode/pause-and-verify/chapter'
import { warningSignsChapter } from '@/learning-paths/incode/warning-signs/chapter'
import { buildDefencesV2Chapter } from '@/learning-paths/incode/build-defences-v2/chapter'
import { whatToDoV2Chapter } from '@/learning-paths/incode/what-to-do-v2/chapter'

export type ChapterEntry = {
  number: number
  basicsSlug: string
  chapter: SlideChapterDefinition
  /**
   * Short prose summary of the chapter's arc, read on the chapter
   * overview page in place of a slide-by-slide table of contents.
   */
  summary: string
  /** 3–4 concrete things the learner walks away with. */
  takeaways: string[]
  /** Arabic for the bilingual (Qatar) face. Slide copy lives with the slides. */
  ar: { title: string; description: string; summary: string; takeaways: string[] }
}

/**
 * Source of truth for the basics curriculum.
 *
 * basicsSlug is the URL slug, distinct from chapter.id (the id is internal and
 * matches the original GranGuard `landscape-of-deception` style). The slugs
 * here match Ken's mockup nav labels.
 */
export const CHAPTERS: ChapterEntry[] = [
  {
    number: 1,
    basicsSlug: 'new-age-of-scams',
    chapter: landscapeOfDeceptionChapter,
    summary:
      'Scams used to be easier to spot: odd wording, strange stories, messages you could dismiss quickly. Today they are more polished, more personal, and often powered by AI. This chapter shows what changed, what scammers are really trying to get, and why calm preparation is a better defense than fear.',
    takeaways: [
      'Why modern scams look more convincing than older ones',
      'How AI helps scammers move faster and sound more believable',
      'What scammers may want besides money',
      'Why a little practice can make you harder to fool',
    ],
    ar: {
      title: 'عصر جديد من الاحتيال',
      description: 'كيف تحوّل الاحتيال إلى صناعة احترافية، ولماذا لم تعد علامات التحذير القديمة تنفع.',
      summary:
        'كان كشف عمليات الاحتيال أسهل في الماضي: صياغة غريبة، قصص لا تُصدَّق، ورسائل يمكنك تجاهلها بسرعة. أما اليوم فهي أكثر إتقاناً وأكثر شخصية، وكثيراً ما تعتمد على الذكاء الاصطناعي. يوضح هذا الفصل ما الذي تغيّر، وما الذي يسعى إليه المحتالون حقاً، ولماذا يُعدّ الاستعداد الهادئ دفاعاً أفضل من الخوف.',
      takeaways: [
        'لماذا تبدو عمليات الاحتيال الحديثة أكثر إقناعاً من القديمة',
        'كيف يساعد الذكاء الاصطناعي المحتالين على التحرك أسرع والظهور بمصداقية أكبر',
        'ما الذي قد يريده المحتالون غير المال',
        'لماذا يجعلك القليل من التدريب أصعب في الخداع',
      ],
    },
  },
  {
    number: 2,
    basicsSlug: 'emotional-hijack',
    chapter: emotionalHijackChapter,
    summary:
      'Scammers do not need you to be careless. They need you to feel rushed, worried, excited, or responsible for someone else. In this chapter, you’ll learn how that pressure works, how to notice it in the moment, and how to slow things down before you act.',
    takeaways: [
      'The two-stage pattern behind many scams',
      'Why scams can work on careful, intelligent people',
      'How to spot when someone is pushing you into fast decisions',
      'Practice recognizing urgency, charity, and tech-support scams',
    ],
    ar: {
      title: 'الاختطاف العاطفي',
      description: 'تعرّف على كيفية استخدام المحتالين لمشاعرك ضدك، والسؤال الواحد الذي يغيّر كل شيء.',
      summary:
        'لا يحتاج المحتالون إلى أن تكون مهملاً. يحتاجون إلى أن تشعر بالاستعجال أو القلق أو الحماس أو المسؤولية تجاه شخص آخر. في هذا الفصل ستتعرّف على كيفية عمل هذا الضغط، وكيف تلاحظه في اللحظة نفسها، وكيف تبطئ الأمور قبل أن تتصرّف.',
      takeaways: [
        'النمط ذو المرحلتين الذي تقوم عليه كثير من عمليات الاحتيال',
        'لماذا تنجح عمليات الاحتيال مع أشخاص حذرين وأذكياء',
        'كيف تكتشف أن شخصاً ما يدفعك إلى قرارات متسرّعة',
        'تدرّب على التعرّف على احتيال الاستعجال والتبرعات والدعم الفني',
      ],
    },
  },
  {
    number: 3,
    basicsSlug: 'pause-and-verify',
    chapter: pauseAndVerifyChapter,
    summary:
      'Most scams depend on one thing: getting you to respond before you have time to think. This chapter teaches a simple habit: pause first, then check through a trusted channel you choose. You’ll practice it with calls, letters, messages, and everyday situations.',
    takeaways: [
      'A two-step habit you can use any time something feels off',
      'How to verify safely without using the link, number, or email they sent',
      'Why the safest check is often outside the conversation',
      'When to act, and when to step away',
    ],
    ar: {
      title: 'توقّف وتحقّق',
      description: 'عادة بسيطة من خطوتين توقف معظم عمليات الاحتيال قبل أن تبدأ.',
      summary:
        'تعتمد معظم عمليات الاحتيال على شيء واحد: أن تستجيب قبل أن تجد وقتاً للتفكير. يعلّمك هذا الفصل عادة بسيطة: توقّف أولاً، ثم تحقّق عبر قناة موثوقة تختارها أنت. ستتدرّب عليها مع المكالمات والرسائل والخطابات والمواقف اليومية.',
      takeaways: [
        'عادة من خطوتين يمكنك استخدامها كلما شعرت أن هناك خطأً ما',
        'كيف تتحقّق بأمان دون استخدام الرابط أو الرقم أو البريد الذي أرسلوه',
        'لماذا يكون التحقّق الأكثر أماناً غالباً خارج المحادثة',
        'متى تتصرّف، ومتى تنسحب',
      ],
    },
  },
  {
    number: 4,
    basicsSlug: 'warning-signs',
    chapter: warningSignsChapter,
    summary:
      'New scams appear all the time, but the warning signs are often familiar. This chapter helps you recognize five common signals, from secrecy and pressure to unusual payment requests. Once you can name the pattern, it becomes much easier to pause and question it.',
    takeaways: [
      'Five scam signals worth taking seriously',
      'Why secrecy is one of the strongest warning signs',
      'How gift cards, crypto, wires, and access requests change the risk',
      'Permission to say no, hang up, or walk away',
    ],
    ar: {
      title: 'علامات التحذير',
      description: 'خمس إشارات احتيال لا ينبغي تجاهلها أبداً.',
      summary:
        'تظهر عمليات احتيال جديدة طوال الوقت، لكن علامات التحذير غالباً ما تكون مألوفة. يساعدك هذا الفصل على التعرّف على خمس إشارات شائعة، من السرية والضغط إلى طلبات الدفع غير المعتادة. وحين تستطيع تسمية النمط، يصبح التوقّف والتساؤل أسهل بكثير.',
      takeaways: [
        'خمس إشارات احتيال تستحق أن تؤخذ على محمل الجد',
        'لماذا تُعدّ السرية من أقوى علامات التحذير',
        'كيف تغيّر بطاقات الهدايا والعملات الرقمية والحوالات وطلبات الوصول مستوى الخطر',
        'الإذن بأن تقول لا، أو تغلق الخط، أو تبتعد',
      ],
    },
  },
  {
    number: 5,
    basicsSlug: 'digital-self-defense',
    chapter: buildDefencesV2Chapter,
    summary:
      'You do not need to become a technology expert to protect yourself online. This chapter focuses on five practical habits that make you harder to target: tightening privacy, strengthening passwords, using extra account protection, knowing how banks can help, and opening official apps safely.',
    takeaways: [
      'How to reduce the personal information scammers can find',
      'Why password reuse creates risk, and what to do instead',
      'How acting quickly can help if money or account access is involved',
      'The safer way to open banks, shops, and official services',
    ],
    ar: {
      title: 'الدفاع الرقمي عن النفس',
      description: 'خمس عادات بسيطة تحمي حساباتك ومالك وراحة بالك.',
      summary:
        'لست بحاجة إلى أن تصبح خبيراً في التقنية لتحمي نفسك على الإنترنت. يركّز هذا الفصل على خمس عادات عملية تجعلك هدفاً أصعب: تشديد الخصوصية، وتقوية كلمات المرور، واستخدام حماية إضافية للحسابات، ومعرفة كيف يمكن للبنوك مساعدتك، وفتح التطبيقات الرسمية بأمان.',
      takeaways: [
        'كيف تقلّل المعلومات الشخصية التي يستطيع المحتالون العثور عليها',
        'لماذا يشكّل تكرار كلمة المرور خطراً، وما البديل',
        'كيف يساعدك التصرّف السريع إذا تعلّق الأمر بالمال أو الوصول إلى الحساب',
        'الطريقة الأكثر أماناً لفتح تطبيقات البنوك والمتاجر والخدمات الرسمية',
      ],
    },
  },
  {
    number: 6,
    basicsSlug: 'if-youre-scammed',
    chapter: whatToDoV2Chapter,
    summary:
      'If something goes wrong, the next step is not blame. It is getting control back. This chapter gives you a clear plan for the first hour, first day, and first week, including who to contact, what to secure, what to record, and how to avoid follow-up scams.',
    takeaways: [
      'What to do first if money or personal information has been shared',
      'Which accounts to secure, and when to contact your bank',
      'What details to save and where to report the scam',
      'How to recognize recovery scams that target people after the fact',
    ],
    ar: {
      title: 'ما تفعله بعد عملية احتيال',
      description: 'كيف تستجيب وتتعافى وتستعيد السيطرة.',
      summary:
        'إذا حدث خطأ ما، فالخطوة التالية ليست اللوم، بل استعادة السيطرة. يمنحك هذا الفصل خطة واضحة للساعة الأولى واليوم الأول والأسبوع الأول، بما في ذلك بمن تتصل، وما الذي تؤمّنه، وما الذي تسجّله، وكيف تتجنّب عمليات الاحتيال اللاحقة.',
      takeaways: [
        'ما تفعله أولاً إذا جرى تحويل مال أو مشاركة معلومات شخصية',
        'أي الحسابات تؤمّنها، ومتى تتصل بالبنك',
        'أي التفاصيل تحتفظ بها، وأين تبلّغ عن الاحتيال',
        'كيف تتعرّف على احتيال استرداد الأموال الذي يستهدف الضحايا لاحقاً',
      ],
    },
  },
]

/** Whole-course length from the chapters' own estimates (about 40 minutes). */
export const COURSE_MINUTES = CHAPTERS.reduce((sum, c) => sum + (c.chapter.estimatedMinutes ?? 0), 0)

export type ChapterText = { title: string; description: string; summary: string; takeaways: string[] }

/** Title/description/summary/takeaways in the given language. */
export function chapterText(entry: ChapterEntry, locale: Locale): ChapterText {
  if (locale === 'ar') return entry.ar
  return {
    title: entry.chapter.title,
    description: entry.chapter.description,
    summary: entry.summary,
    takeaways: entry.takeaways,
  }
}

export function useChapterText(entry: ChapterEntry): ChapterText {
  const text = chapterText(entry, useLocale())
  // Titles are US English at source; the Qatar face's English follows UK
  // spelling (Digital Self-Defence), the same rule useSlideCopy applies.
  return resolveSite() === 'qatar' ? { ...text, title: localizeCopy(text.title, 'GB') } : text
}

export function getChapterBySlug(slug: string): ChapterEntry | null {
  return CHAPTERS.find(c => c.basicsSlug === slug) ?? null
}

export function getNextChapterSlug(currentSlug: string): string | null {
  const i = CHAPTERS.findIndex(c => c.basicsSlug === currentSlug)
  if (i === -1 || i === CHAPTERS.length - 1) return null
  return CHAPTERS[i + 1].basicsSlug
}
